import React, { useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  RedditShareButton,
  EmailShareButton,
  FacebookIcon,
  XIcon,
  WhatsappIcon,
  RedditIcon,
  EmailIcon,
} from 'react-share';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

const ICON_SIZE = 40;

const PLATFORMS = [
  { id: 'facebook', label: 'Facebook', Icon: FacebookIcon },
  { id: 'x', label: 'X', Icon: XIcon },
  { id: 'whatsapp', label: 'WhatsApp', Icon: WhatsappIcon },
  { id: 'reddit', label: 'Reddit', Icon: RedditIcon },
  { id: 'email', label: 'Email', Icon: EmailIcon },
];

/**
 * Vertical share-target list modal (Facebook, X, WhatsApp, Reddit, Email)
 * with a full-width copy-link CTA below. URL construction handled by react-share.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.url - canonical listing URL
 * @param {string} props.title - listing title
 * @param {string} [props.priceLabel]
 * @param {string} [props.conditionLabel]
 * @param {string} [props.location]
 */
const ShareModal = ({ open, onClose, url, title, priceLabel, conditionLabel, location }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const detailParts = [conditionLabel, location].filter(Boolean);
  const suffix = detailParts.length ? ` (${detailParts.join(', ')})` : '';
  const shareText = [title, priceLabel].filter(Boolean).join(' · ') + suffix;

  const handleCopy = () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(url || '').then(() => setCopied(true)).catch(() => {});
  };

  const rowClass =
    'flex flex-row items-center gap-4 py-3 px-5 sm:px-6 w-full hover:bg-accent/40 transition-colors';
  const labelClass =
    'text-xs font-heading tracking-widest uppercase text-foreground';
  const blockStyle = { display: 'block', width: '100%' };

  const renderInner = ({ Icon, label }) => (
    <span className={rowClass} style={{ textDecoration: 'none' }}>
      <Icon size={ICON_SIZE} round />
      <span className={labelClass}>{label}</span>
    </span>
  );

  const renderButton = ({ id, label, Icon }) => {
    const inner = renderInner({ Icon, label });
    if (id === 'facebook') {
      return (
        <FacebookShareButton url={url} style={blockStyle}>
          {inner}
        </FacebookShareButton>
      );
    }
    if (id === 'x') {
      return (
        <TwitterShareButton url={url} title={shareText} style={blockStyle}>
          {inner}
        </TwitterShareButton>
      );
    }
    if (id === 'whatsapp') {
      return (
        <WhatsappShareButton url={url} title={shareText} separator=" " style={blockStyle}>
          {inner}
        </WhatsappShareButton>
      );
    }
    if (id === 'reddit') {
      return (
        <RedditShareButton url={url} title={title} style={blockStyle}>
          {inner}
        </RedditShareButton>
      );
    }
    return (
      <EmailShareButton
        url={url}
        subject={title}
        body={`${shareText}\n`}
        style={blockStyle}
      >
        {inner}
      </EmailShareButton>
    );
  };

  return (
    <Dialog open={open} onOpenChange={isOpen => { if (!isOpen) onClose(); }}>
      <DialogContent
        aria-describedby={undefined}
        className="p-0 gap-0 overflow-hidden bg-background border border-border max-w-[42rem] sm:max-w-[42rem] w-[calc(100vw-1rem)]"
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b border-border text-left">
          <DialogTitle className="font-heading uppercase tracking-widest text-sm">
            Share Listing
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1 pr-6">{title}</p>
        </DialogHeader>

        <div className="flex flex-col divide-y divide-border border-b border-border">
          {PLATFORMS.map(p => (
            <div key={p.id} className="flex items-stretch min-w-0">
              {renderButton(p)}
            </div>
          ))}
        </div>

        <div className="p-4 sm:p-6 space-y-3">
          <div className="border border-border rounded-md overflow-hidden">
            <span className="block px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm text-muted-foreground font-mono bg-muted/40 truncate select-all">
              {url}
            </span>
          </div>
          <Button
            type="button"
            onClick={handleCopy}
            className="w-full font-heading uppercase tracking-widest text-xs h-11 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:opacity-90"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" /> Copy link
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
